import { Flex, Icon, VStack, Heading, Text, Box, HStack, Separator, Center, Skeleton } from "@chakra-ui/react"
import { AssetStatus, TenantStatusCopyType } from "@hydrologiq/gql-experience/graphql"
import { statusToText } from "@hydrologiq/shared/asset/attributes/status"
import { useCopyStatuses } from "@hydrologiq/shared/providers/helpers/useCopy"
import { AssetInfo, OperationalStatus, SystemNodeAttributes, NodeAlertSummary, LoadPrecedence } from "../../../types/SystemView"
import { getStatusColor } from "../../../utils/getStatusColor"
import { nodeRowCount, splitLoadPrecedence, toAssetStatus } from "../../../utils/systemTransform"
import { Switch } from "@hydrologiq/shared/chakra/Switch"
import { Tooltip } from "@hydrologiq/shared/chakra/Tooltip"
import { IconType } from "react-icons"
import { useLazyQuery } from "@apollo/client"
import { GetAssetMonitoringMultiFieldTimeseriesDocument, InstallationDataWindowedCombinedDocument, IoTDataLatestDocument } from "@hydrologiq/gql/graphql"
import { useEffect, useRef, useState } from "react"
import { useLocation } from "react-router-dom"
import { DateTime, hours, milliseconds, minutes, seconds } from "timezonecomplete"
import { IANATimeZone, startOfDay, toTimestamp } from "@hydrologiq/common/helpers/date"
import { energy } from "@hydrologiq/common/transformations/data/energy"
import { EnergyUnit } from "@hydrologiq/common/types/energy/consumption"
import { useIsSectionEnabled } from "@hydrologiq/shared/providers/SectionFeatures"
import AssetLink from "@hydrologiq/shared/asset/AssetLink"
import { Asset } from "@hydrologiq/gql-experience/graphql"
import { getNodeDimensions, NODE_ROW_HEIGHT } from "@hydrologiq/systems/types/SystemNode"
import { SystemNodeVersion } from "@hydrologiq/systems/types/SystemNode"
import { InlineSparkline } from "../../monitoring/InlineSparkline"
import { useGetFeature } from "@hydrologiq/shared/providers/helpers/useFeature"
import AlertBadge from "./AlertBadge"
import LoadPrecedenceRows, { LoadPrecedenceValue } from "./LoadPrecedenceRows"
import { useNodeLoadPrecedence } from "../../../providers/loadPrecedenceStore"
import { useLiveValue, useLiveAttributeValue } from "../../../providers/helpers/useSystem"

export interface ApiAssetAssignment {
  id: string
  status: "active" | "inactive" | null
  monitoringInstanceId?: string
}

export interface ApiAssetInlineGraphProps {
  attributes?: ApiAssetAttributeProps[]
}

export interface ApiAssetAttributeProps extends SystemNodeAttributes {
  installation?: string
  start: DateTime
  end: DateTime
  offline?: boolean
  onUpdate?: () => void
}

// Unavailable (CONTEXT.md): a dash, never zero
const UNAVAILABLE = "—"
const NUMERIC = /^-?\d+(\.\d+)?(e-?\d+)?$/i

export interface ApiAssetImplProps {
  attributes?: ApiAssetAttributeProps[]
  statusToggle: () => void
  icon?: IconType
  name: string
  disabled?: boolean
  assignment?: ApiAssetAssignment
  assignmentStatus: string
  operationalStatus?: OperationalStatus
  workflowState: string
  controllable?: boolean
  asset?: AssetInfo
  installation?: string
  alerts?: NodeAlertSummary
  loadPrecedence?: LoadPrecedence
}

function ApiAssetAttribute(props: ApiAssetAttributeProps) {
  const { name, unit, field, slot, attribute, installation, asset, start, end, thresholds, installationOnly, onUpdate } = props
  let { value } = props

  // When the system's monitor source is 'timescale', the backend resolves the
  // latest IoT value from TimescaleDB and pushes it into the LiveValuesStore on
  // every 45s poll. useLiveValue subscribes at the leaf level — only this
  // component re-renders, never the parent node or the ReactFlow canvas.
  const liveValue = useLiveValue(asset?.id, field)
  const serverProvided = liveValue !== undefined

  // Subscribe to server-resolved attribute values (e.g. custom asset attributes
  // like "production") which are refreshed on every 45s poll.
  const liveAttributeValue = useLiveAttributeValue(asset?.id, attribute)

  // Hook must be called unconditionally (React rules of hooks)
  const copy = useCopyStatuses(TenantStatusCopyType.Asset)

  const [getLatestValue, { data, error }] = useLazyQuery(IoTDataLatestDocument)
  const { pathname } = useLocation()
  const lastDataValueRef = useRef<string | null>(null)
  const onUpdateRef = useRef(onUpdate)
  onUpdateRef.current = onUpdate
  const hasNotifiedRef = useRef(false)

  useEffect(() => {
    hasNotifiedRef.current = false
    if (!serverProvided && field && start && end) {
      getLatestValue({
        variables: {
          installations: asset && !installationOnly ? "" : installation ?? "",
          assets: asset?.id || "",
          start: toTimestamp(start),
          stop: toTimestamp(end),
          field: field,
          location: IANATimeZone(),
        },
        pollInterval: 30000,
        notifyOnNetworkStatusChange: true,
      })
    }
  }, [getLatestValue, installation, asset, start, end, pathname, serverProvided])

  useEffect(() => {
    if (serverProvided) {
      // Server-provided values change on each 45s poll; notify so the
      // "Updated" timestamp refreshes.
      const currentValue = liveValue?.toString() ?? null
      if (!hasNotifiedRef.current || currentValue !== lastDataValueRef.current) {
        lastDataValueRef.current = currentValue
        hasNotifiedRef.current = true
        onUpdateRef.current?.()
      }
      return
    }
    if (data?.getIoTDataLatest) {
      const currentValue = data.getIoTDataLatest.value?.toString() ?? null
      if (!hasNotifiedRef.current || currentValue !== lastDataValueRef.current) {
        lastDataValueRef.current = currentValue
        hasNotifiedRef.current = true
        onUpdateRef.current?.()
      }
    }
  }, [data, serverProvided, liveValue])

  // Attribute-keyed values (status/location/custom) refresh via the 45s poll
  // store — notify so the "Updated" timestamp refreshes when they change.
  const lastAttributeValueRef = useRef<string | null>(null)
  const hasNotifiedAttributeRef = useRef(false)
  useEffect(() => {
    if (attribute === undefined || liveAttributeValue === undefined) return
    const currentValue = liveAttributeValue ?? null
    if (!hasNotifiedAttributeRef.current || currentValue !== lastAttributeValueRef.current) {
      lastAttributeValueRef.current = currentValue
      hasNotifiedAttributeRef.current = true
      onUpdateRef.current?.()
    }
  }, [attribute, liveAttributeValue])

  if (serverProvided) {
    if (liveValue !== null && liveValue !== undefined) {
      let floatValue = liveValue
      if (field?.includes("power")) floatValue = energy(EnergyUnit.W, EnergyUnit.kW, floatValue)
      value = floatValue.toFixed(2)
    } else value = UNAVAILABLE
  } else if (data) {
    if (data.getIoTDataLatest && data.getIoTDataLatest.value) {
      let floatValue = parseFloat(data.getIoTDataLatest.value.toString())
      if (field?.includes("power")) floatValue = energy(EnergyUnit.W, EnergyUnit.kW, floatValue)
      value = floatValue.toFixed(2)
    } else value = "0"
  }
  if (error) value = "0"

  // For the "status" attribute, prefer the live asset.status from the 45s poll
  // store (raw status string) over the baked-in lifecycleStatus so status
  // changes (e.g. "outbound" → "deployed") reflect without a full refetch.
  const liveLifecycleStatus =
    attribute === "status" && liveAttributeValue !== undefined && liveAttributeValue !== null
      ? toAssetStatus(liveAttributeValue)
      : undefined
  const effectiveLifecycleStatus = liveLifecycleStatus ?? asset?.lifecycleStatus

  if (attribute) {
    if (attribute === "location") {
      // Prefer live location name from the poll store, else baked-in value.
      value = (liveAttributeValue !== undefined && liveAttributeValue !== null ? liveAttributeValue : asset?.location?.name) ?? "n/a"
    } else if (attribute === "status") {
      value = statusToText(effectiveLifecycleStatus ?? AssetStatus.Unknown, copy)
    } else if (liveAttributeValue !== undefined && liveAttributeValue !== null) {
      // Use the server-resolved value from the 45s poll store
      value = liveAttributeValue
    }
    // else: fall through to the initial `value` prop from the node data
    // asset_attribute stores numbers unrounded ("6.8591999999999995"); show them as field values are shown
    if (attribute !== "location" && attribute !== "status" && value && NUMERIC.test(value)) value = Number(value).toFixed(2)
  }
  if (!value) value = "0"

  const valueInt = parseInt(value)
  const applyThresholds = (data || serverProvided) && thresholds && typeof valueInt === "number"

  const inThreshold = (min?: number, max?: number) =>
    min && max ? valueInt >= min && valueInt < max : min ? valueInt >= min : max ? valueInt <= max : false
  const criticalThreshold = inThreshold(undefined, thresholds?.critical) ? "#BF0000" : undefined
  const lowThreshold = inThreshold(undefined, thresholds?.low) ? "#a34a0f" : undefined
  const okThreshold = inThreshold(undefined, thresholds?.ok) ? "#BF9700" : undefined
  const highThreshold = inThreshold(undefined, thresholds?.high) ? "#80b738" : undefined
  const peakThreshold = inThreshold(thresholds?.peak) ? "#00BF00" : undefined

  const backgroundColor = criticalThreshold || lowThreshold || okThreshold || highThreshold || peakThreshold
  const textColor = lowThreshold || okThreshold ? "white" : undefined

  const statusColor = attribute === "status" ? getStatusColor(effectiveLifecycleStatus) : undefined

  return (
    <HStack
      fontSize="8px"
      width="100%"
    >
      <HStack background={statusColor ? undefined : backgroundColor} color={statusColor ? undefined : textColor} borderRadius="3px" padding="0 2px" gap={0.5}>
        <Text>{name}:</Text>
        <HStack fontWeight="bold" gap={0.45} color={statusColor}>
          <Tooltip content={value} positioning={{ placement: "bottom-end" }}>
            <Text truncate maxWidth="50px">
              {value}
            </Text>
          </Tooltip>
          {value !== UNAVAILABLE && <Text>{unit}</Text>}
        </HStack>
      </HStack>
    </HStack>
  )
}

export interface ApiAssetInlineGraphProps {
  field: string
  asset: string
  stop: DateTime
  onUpdate?: () => void
}
export function ApiAssetInlineGraph({ field, asset, stop, onUpdate }: ApiAssetInlineGraphProps) {
  const [getData, { data, error }] = useLazyQuery(GetAssetMonitoringMultiFieldTimeseriesDocument)
  const [loaded, setLoaded] = useState(false)
  const { pathname } = useLocation()
  const now = DateTime.now()
  const end = now.lessThan(stop) ? now : stop
  // Floor to hour not using start
  const start = end.sub(hours(12)).sub(minutes(end.minute())).sub(seconds(end.second())).sub(milliseconds(end.millisecond()))

  useEffect(() => {
    if (field && start && end) {
      getData({
        variables: {
          id: asset ?? "",
          start: toTimestamp(start),
          stop: toTimestamp(end),
          fields: { source: { field: field, agg: "mean" } },
          granularity: "5m",
          location: IANATimeZone(),
        },
        // No poll: the window above is fixed when this effect runs, so polling only
        // re-fetched the same 12 hours every 30 s. https://github.com/hydrologiq/core/issues/119
        notifyOnNetworkStatusChange: true,
        onCompleted: () => {
          onUpdate?.()
          setLoaded(true)
        },
      })
    }
  }, [getData, asset, stop, pathname])


  if (data) {
    const inlineData = data.values?.map((value) => ({ name: value?.time, value: value?.value })).filter((value) => value.name && value.value) as Array<{ name: string; value: number }>
    return (
      <InlineSparkline
        data={inlineData}
        start={start}
        end={end}
        sampleIntervalMs={5 * 60 * 1000}
        unit={field === "power" ? "kW" : field === "weight" ? "kg" : field === "pressure" ? "bar" : "n/a"}
        field={field}
      />
    )
  }
  if (!loaded) {
    <Skeleton height="56px" width="100%" />
  }
  return <Center height={"56px"} width="100%">
    <Text fontSize="8px">Waiting on live graph data to be available.</Text>
  </Center>
}

const GRAPH_ATTRIBUTES = ["weight", "pressure", "power"]

function ApiAssetImpl(props: ApiAssetImplProps) {
  const {
    attributes,
    statusToggle,
    icon,
    name,
    disabled,
    assignment,
    assignmentStatus,
    workflowState,
    controllable,
    asset,
    operationalStatus,
    installation,
    alerts,
    loadPrecedence,
  } = props
  const attributesSlots = (attributes ?? []).reduce((acc, attribute) => {
    const slot = attribute.slot
    if (typeof slot === "number") {
      acc[slot] = attribute
    }
    return acc
  }, {} as Record<number, ApiAssetAttributeProps>)

  const isActive = assignmentStatus === "active" || (operationalStatus && operationalStatus === "active")
  // A complex asset has no telemetry or monitoring of its own, so its assignment stays
  // inactive while its modules report. Its derived attributes are the modules' values.
  const showsData = isActive || (attributes?.some((a) => a.derivedFrom) ?? false)

  // The graph queries the node's own asset id, so a derived attribute (a complex asset's
  // Power, taken from its modules) has nothing to plot there.
  const graphable = attributes?.filter((a) => !a.derivedFrom)
  const graphField = GRAPH_ATTRIBUTES.find((attribute) => graphable?.some((a) => a.field === attribute))
  const graphAttribute = graphable?.find((a) => a.field === graphField)



  const isAssetsSectionUsed = useIsSectionEnabled("assets")

  const graphsEnabled = useGetFeature("system_node_graphs")
  const hasMonitoring = controllable && assignment && assignment.monitoringInstanceId
  // Sized by the verdict the page loaded with, as systemTransform sizes the ReactFlow node
  const { width, height } = getNodeDimensions(SystemNodeVersion.V2, controllable ?? false, graphsEnabled, nodeRowCount({ loadPrecedence, asset, attributes }))
  // Each poll's verdict, once the store has one
  const latestPrecedence = useNodeLoadPrecedence(asset?.id) ?? loadPrecedence
  const precedence = latestPrecedence?.entries.length ? splitLoadPrecedence(latestPrecedence.entries, asset?.id, attributes) : undefined
  // A group's node carries its Modules' entries, so name each Asset there.
  const showPrecedenceAssetNames = latestPrecedence?.entries.some((entry) => entry.assetId !== asset?.id) ?? false
  // One attribute per line in slot order, leaving out empty slots
  const slots = [0, 1, 2, 3].filter((slot) => slot in attributesSlots)


  return (
    <Box position="relative" width={width} height={height}>
      {alerts && asset && <AlertBadge alerts={alerts} assetId={asset.id} assetName={name} />}
      <VStack width={width} height={height} alignItems="stretch" gap={1} color="primary" px={3}>
        <HStack gap={"3px"} justifyContent="space-between" pt={"6px"}>
          <HStack justifyContent="space-between" gap={"5px"} alignItems="center">
            <Icon height="15px" width="15px" as={icon} />
            {isAssetsSectionUsed ? <span className="nodrag"><AssetLink size="2xs" truncate={true} maxWidth={`${width - 100}px`} key={`${asset?.id}-${name}-${isActive}`} asset={{ id: asset?.id || "", name: name || "", monitoring_status: isActive ? "active" : "inactive" } as Asset} iconSize={9.5} openExternal={true} tooltip={true} /></span> : <Heading as={"h3"} fontSize="9px" truncate maxWidth={isActive && 0 in attributesSlots ? "90px" : "145px"}>
              {name}
            </Heading>}
          </HStack>
          {controllable && assignment && assignment.monitoringInstanceId && (
            <HStack alignItems="center" gap={0.5}>
              <VStack alignItems="stretch" gap={0} height="20px" justifyContent="center">
                <Text fontSize="8px">Monitoring</Text>
              </VStack>
              <Switch
                mb={-0.5}
                onClick={(e) => {
                  e.preventDefault()
                  if (!disabled) {
                    statusToggle()
                  }
                }}
                checked={isActive}
                defaultChecked={false}
                disabled={disabled || workflowState === "processing" || workflowState === "confirming"}
                inputProps={{
                  "aria-label": isActive ? "Deactivate" : "Activate",
                }}
                compact
              />
            </HStack>
          )}
        </HStack>
        <Separator width="100%" borderColor="grey_light" />
        {showsData ? <>
          <VStack alignItems="stretch" gap={0}>
            {slots.map((slot) => {
              const attribute = attributesSlots[slot]
              return (
                <Box key={slot} data-testid="node-attribute" height={`${NODE_ROW_HEIGHT}px`}>
                  {attribute.field === "power" && precedence?.power
                    ? <LoadPrecedenceValue name={attribute.name} entry={precedence.power} />
                    : <ApiAssetAttribute {...attribute} asset={asset} installation={installation} />}
                </Box>
              )
            })}
          </VStack>
          {graphsEnabled && asset && graphField && graphAttribute && <>
            <ApiAssetInlineGraph field={graphField} asset={asset?.id} stop={graphAttribute.end} /></>}
          {precedence && precedence.rows.length > 0 && (
            <LoadPrecedenceRows entries={precedence.rows} showAssetNames={showPrecedenceAssetNames} />
          )}</> :
          <Center height="100%" >
            <Text fontSize="10px">{hasMonitoring ? "Activate monitoring to see asset data" : "Asset data not available"}</Text>
          </Center>
        }
      </VStack>
    </Box>
  )
}

export default ApiAssetImpl
